/**
 * Diamonds review panel
 *
 * Lists every candle the rule fires on, plus any you've marked as missed, and
 * lets you label each one:
 *   Yes    — the rule was right
 *   No     — it was wrong, pick or type a reason
 *   Missed — added by hand from a candle the rule rejected
 *
 * For a missed candle it shows exactly which condition rejected it and by how
 * much, which is the whole point: that's what tells us how to fix the rule.
 *
 * Thresholds are editable inline and recompute the list instantly — no engine
 * rebuild — so you can watch the candidate count move as you tighten them.
 */

import React, { useMemo, useState } from 'react';
import { X, Ban, Plus, RotateCcw } from 'lucide-react';
import { useCandidateStore } from '../store/candidateStore';
import {
  ReviewCandidate,
  CandidateLabelMap,
  CandidateThresholds,
  CandidateVerdict,
  AutoTagMap,
  collectReasonPresets,
  GRADES,
  labelAgreement,
  partitionByLabel,
  verdictForGrade,
} from '../utils/setupCandidates';
import CandidateThresholdPreview from './CandidateThresholdPreview';
import './CandidateReviewPanel.css';

interface CandidateReviewPanelProps {
  candidates: ReviewCandidate[];
  /** Suggestions for unlabelled candidates, derived from your hand labels. */
  autoTags: AutoTagMap;
  onClose: () => void;
  /** Scroll/centre the chart on a candidate. */
  onJumpTo?: (timestamp: number) => void;
}

type Filter = 'all' | 'todo' | 'super-strong' | 'strong' | 'moderate' | 'weak' | 'no' | 'missed';

/** "Jul 30, 11:32 AM" — date matters once the list spans more than one session. */
function fmtWhen(ts: number): string {
  const d = new Date(ts);
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(
    [],
    { hour: '2-digit', minute: '2-digit' }
  )}`;
}

const CandidateReviewPanel: React.FC<CandidateReviewPanelProps> = ({
  candidates,
  autoTags,
  onClose,
  onJumpTo,
}) => {
  const labels = useCandidateStore((s) => s.labels);
  const thresholds = useCandidateStore((s) => s.thresholds);
  const selectedId = useCandidateStore((s) => s.selectedId);
  const setThresholds = useCandidateStore((s) => s.setThresholds);
  const reloadThresholds = useCandidateStore((s) => s.reloadThresholds);
  const historyStatus = useCandidateStore((s) => s.historyStatus);
  const historyError = useCandidateStore((s) => s.historyError);
  const historyCoverage = useCandidateStore((s) => s.historyCoverage);
  const label = useCandidateStore((s) => s.label);
  const select = useCandidateStore((s) => s.select);
  const autoTagEnabled = useCandidateStore((s) => s.autoTagEnabled);
  const setAutoTagEnabled = useCandidateStore((s) => s.setAutoTagEnabled);
  const autoTagOptions = useCandidateStore((s) => s.autoTagOptions);
  const setAutoTagOptions = useCandidateStore((s) => s.setAutoTagOptions);
  const acceptAutoTag = useCandidateStore((s) => s.acceptAutoTag);
  const scoreFrom = useCandidateStore((s) => s.scoreFrom);
  const scoreFromNow = useCandidateStore((s) => s.scoreFromNow);
  const recalculate = useCandidateStore((s) => s.recalculate);
  const [recalcing, setRecalcing] = React.useState(false);
  const lookbackDays = useCandidateStore((s) => s.lookbackDays);
  const setLookbackDays = useCandidateStore((s) => s.setLookbackDays);
  const autoCount = Object.keys(autoTags).length;
  // Your vocabulary, not a canned one — grows as you type new reasons.
  const presets = useMemo(() => collectReasonPresets(labels), [labels]);

  const [filter, setFilter] = useState<Filter>('todo');
  const [reasonDraft, setReasonDraft] = useState<Record<string, string>>({});
  const [needsReason, setNeedsReason] = useState<Record<string, boolean>>({});

  // A downvote without a reason teaches nothing; positive votes don't need one.
  const vote = (id: string, verdict: CandidateVerdict, why: string): void => {
    if (verdict === 'not-ideal' && !why.trim()) {
      setNeedsReason((d) => ({ ...d, [id]: true }));
      return;
    }
    setNeedsReason((d) => ({ ...d, [id]: false }));
    label(id, verdict, why);
  };

  const score = useMemo(() => labelAgreement(candidates, labels), [candidates, labels]);
  const parts = useMemo(() => partitionByLabel(candidates, labels), [candidates, labels]);

  const visible = useMemo(() => {
    const pick = () => {
      switch (filter) {
        case 'todo':
          return parts.unlabelled;
        case 'super-strong':
          return parts.superStrong;
        case 'strong':
          return parts.strong;
        case 'moderate':
          return parts.moderate;
        case 'weak':
          return parts.weak;
        case 'no':
          return parts.notIdeal;
        case 'missed':
          return parts.missed;
        default:
          return candidates;
      }
    };
    // Newest first: you review what just happened, and the backlog grows off
    // the bottom rather than pushing the live bars down the list.
    return [...pick()].sort((a, b) => b.timestamp - a.timestamp);
  }, [filter, parts, candidates]);

  // Only ever rendered inside the `thresholds &&` branch below; the fallback is
  // for the type checker, which cannot narrow through the closure.
  const num = (key: keyof CandidateThresholds, step: number, min: number) => (
    <input
      type="number"
      className="crp-input"
      step={step}
      min={min}
      value={(thresholds?.[key] as number) ?? 0}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (Number.isFinite(v)) setThresholds({ [key]: v } as Partial<CandidateThresholds>);
      }}
    />
  );

  /**
   * Where the engine's history fetch got to.
   *
   * With no local scorer there is no such thing as "the chart worked it out
   * anyway": if this fetch has not landed, the list below is empty and the only
   * honest thing to do is say why. An empty list and a failed fetch look
   * identical otherwise.
   */
  const historyNote = ((): string | null => {
    if (historyStatus === 'loading') return 'loading engine scores…';
    if (historyStatus === 'error') return `engine unavailable — ${historyError ?? 'fetch failed'}`;
    if (historyStatus === 'idle') return 'engine not asked yet';
    if (!historyCoverage) return null;
    const { days, daysRequested, candlesInWindow, candlesJudged } = historyCoverage;
    const bits: string[] = [];
    // The engine ran out of loaded sessions before it ran out of the window
    // asked for.
    if (days < daysRequested) bits.push(`only ${days} of ${daysRequested} sessions loaded`);
    // Bars in the window with no trailing data. On a freshly started engine
    // this is most of them, and it is the difference between "no diamond here"
    // and "nobody looked here".
    if (candlesJudged < candlesInWindow) {
      bits.push(`${candlesInWindow - candlesJudged} of ${candlesInWindow} bars unjudgeable (no trailing data)`);
    }
    return bits.length ? bits.join(' · ') : null;
  })();

  return (
    <div className="crp-panel">
      <div className="crp-header">
        <span className="crp-title">Diamonds</span>
        <button className="crp-close" onClick={onClose} title="Close">
          <X size={13} />
        </button>
      </div>

      <div className="crp-rule">
        {/* The rule lives on the engine and is null here until it answers.
            There is no local default set to fall back on any more — the one
            that used to be here had drifted from the engine's (50/25 against
            15/20), so a browser that had not heard back displayed, and could
            push back, a rule nothing was trading. */}
        {!thresholds && (
          <div className="crp-rule-row crp-muted">waiting for the engine's rule…</div>
        )}
        {thresholds && (
          <>
        <div className="crp-rule-row">
          <span>Child body &lt;</span>
          {num('childBodyMaxPctOfParentBody', 1, 0)}
          <span>% of parent BODY</span>
        </div>
        <div className="crp-rule-row">
          <span>Child body &ge;</span>
          {num('childBodyMinPctOfParentBody', 1, 0)}
          <span>% of parent BODY</span>
        </div>
        <div className="crp-rule-row">
          <span>Parent body &ge;</span>
          {num('parentBodyMinPct', 1, 0)}
          <span>% of its range</span>
        </div>
        <label className="crp-rule-row crp-check">
          <input
            type="checkbox"
            checked={thresholds.hodLodEnabled}
            onChange={(e) => setThresholds({ hodLodEnabled: e.target.checked })}
          />
          <span>Score down near HOD/LOD</span>
        </label>
        {thresholds.hodLodEnabled && (
          <>
            <div className="crp-rule-row">
              <span>within</span>
              {num('hodLodRangePoints', 5, 0)}
              <span>pts of the extreme</span>
            </div>
            <div className="crp-rule-row">
              <span>deduct up to</span>
              {num('hodLodPenalty', 1, 0)}
              <span>score</span>
            </div>
          </>
        )}
        {/* The absolute floor, ABOVE the ratio row because it runs first: a
            bar under it is never examined, so the ratio never gets a say.
            Independent of `useAtrFilter` on purpose — that flag governs the
            ratio, and folding a second meaning into it would make one
            checkbox silently control two different rules. */}
        <div className="crp-rule-row">
          <span>Skip unless ATR &ge;</span>
          {num('minAtr', 0.5, 0)}
          <span>pts</span>
        </div>

        <label className="crp-rule-row crp-check">
          <input
            type="checkbox"
            checked={thresholds.useAtrFilter}
            onChange={(e) => setThresholds({ useAtrFilter: e.target.checked })}
          />
          <span>Also gate on ATR (didn't separate in your labels)</span>
        </label>
        {thresholds.useAtrFilter && (
          <div className="crp-rule-row">
            <span>Parent range &ge;</span>
            {num('atrMult', 0.01, 0)}
            <span>&times; ATR</span>
          </div>
        )}
        <label className="crp-rule-row crp-check">
          <input
            type="checkbox"
            checked={thresholds.requireDirectionAgree}
            onChange={(e) => setThresholds({ requireDirectionAgree: e.target.checked })}
          />
          <span>Trailing direction must match parent</span>
        </label>
          </>
        )}
        <label className="crp-rule-row crp-check">
          <input
            type="checkbox"
            checked={autoTagEnabled}
            onChange={(e) => setAutoTagEnabled(e.target.checked)}
          />
          <span>Auto-tag the rest from my labels</span>
        </label>
        {autoTagEnabled && (
          <div className="crp-rule-row">
            <span>confidence &ge;</span>
            <input
              type="number"
              className="crp-input"
              step={0.05}
              min={0}
              max={1}
              value={autoTagOptions.minConfidence}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (Number.isFinite(v)) setAutoTagOptions({ minConfidence: v });
              }}
            />
            <span>· {autoCount} suggested</span>
          </div>
        )}
        <div className="crp-rule-row crp-from">
          <span>show</span>
          {([
            [0, 'today'],
            [1, '2d'],
            [4, '5d'],
            [-1, 'all'],
          ] as [number, string][]).map(([d, label]) => (
            <button
              key={label}
              className={`crp-preset ${lookbackDays === d ? 'on' : ''}`}
              onClick={() => setLookbackDays(d)}
            >
              {label}
            </button>
          ))}
          <button className="crp-preset" onClick={scoreFromNow} title="Score only from this moment">
            now
          </button>
          {/* The rule lives server-side. This asks the engine for it again and
              redraws — picks up a change made from the API, another browser,
              or a model retrain, none of which this tab would otherwise see. */}
          <button
            className="crp-preset"
            disabled={recalcing}
            onClick={async () => {
              setRecalcing(true);
              try {
                await recalculate();
              } finally {
                setRecalcing(false);
              }
            }}
            title="Re-pull the rule from the engine and redraw every diamond"
          >
            {recalcing ? '…' : 'recalc'}
          </button>
        </div>
        <div className="crp-rule-row crp-from">
          <span>
            from {scoreFrom === 0
              ? 'all loaded history'
              : new Date(scoreFrom).toLocaleString([], {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
          </span>
        </div>
        {/* "Reset" is now "take the engine's saved rule". It used to restore a
            set of constants held in the frontend, which had drifted from the
            engine's own defaults — so resetting moved the chart FURTHER from
            what the bot trades. Those constants are deleted. */}
        <button
          className="crp-reset"
          onClick={() => void reloadThresholds()}
          title="Discard local edits and take the engine's saved rule"
        >
          <RotateCcw size={10} /> reload rule
        </button>
        {historyNote && <div className="crp-rule-row crp-muted">{historyNote}</div>}
      </div>

      {thresholds && <CandidateThresholdPreview thresholds={thresholds} />}

      <div className="crp-score">
        <span>{candidates.length} diamonds</span>
        <span className="crp-dot">·</span>
        <span className="crp-super-strong">{score.superStrong} super</span>
        <span className="crp-strong">{score.strong} strong</span>
        <span className="crp-moderate">{score.moderate} mod</span>
        <span className="crp-weak">{score.weak} weak</span>
        <span className="crp-no">{score.notIdeal} no</span>
        <span className="crp-missed">{score.missed} missed</span>
        {score.labelled > 0 && (
          <>
            <span className="crp-dot">·</span>
            <span title="Of the ones it fired on, how many were a setup at any grade">
              precision {score.precisionPct.toFixed(0)}%
            </span>
            <span title="Of the ones it fired on, how many were STRONG">
              strong {score.strongPrecisionPct.toFixed(0)}%
            </span>
            <span title="Of the setups you say exist, how many it caught">
              recall {score.recallPct.toFixed(0)}%
            </span>
          </>
        )}
      </div>

      <div className="crp-filters">
        {(['todo', 'super-strong', 'strong', 'moderate', 'weak', 'no', 'missed', 'all'] as Filter[]).map((f) => (
          <button
            key={f}
            className={`crp-filter ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'todo' ? `auto (${parts.unlabelled.length})` : f === 'super-strong' ? 'super' : f}
          </button>
        ))}
      </div>

      <div className="crp-list">
        {visible.length === 0 && (
          <div className="crp-empty">
            {filter === 'todo'
              ? 'Nothing waiting — every candidate carries the indicator\u2019s call.'
              : 'Nothing here yet.'}
          </div>
        )}

        {visible.map((c) => {
          const l: CandidateLabelMap[string] | undefined = labels[c.id];
          const isSelected = selectedId === c.id;
          return (
            <div
              key={c.id}
              className={`crp-row ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                select(c.id);
                onJumpTo?.(c.timestamp);
              }}
            >
              <div className="crp-row-head">
                <span className={`crp-dir ${c.direction ?? 'none'}`}>
                  {(c.direction ?? '—').toUpperCase()}
                </span>
                <span className="crp-time">{fmtWhen(c.timestamp)}</span>
                {c.source === 'manual' && <span className="crp-tag missed">missed</span>}
                {l && <span className={`crp-tag ${l.verdict}`}>{l.verdict}</span>}
                {!l && autoTags[c.id] && (
                  <span className={`crp-tag auto ${autoTags[c.id].verdict}`}>
                    auto {autoTags[c.id].verdict} {Math.round(autoTags[c.id].confidence * 100)}%
                  </span>
                )}
                {/* The engine's grade, not a locally predicted one. A rejected
                    bar has none, and that row shows its rejection instead. */}
                {!l && !autoTags[c.id] && c.grade && (
                  <span className={`crp-tag auto ${verdictForGrade(c.grade)}`}>
                    auto {c.grade}
                  </span>
                )}
              </div>

              {/* Every number here came off the engine's payload. Nullable
                  because a bar it rejected before measuring anything (no
                  trailing bar, ATR not warm) genuinely has none, and printing
                  zeroes would read as measurements. */}
              <div className="crp-metrics">
                {c.score !== null && (
                  <span className="crp-score-chip">{c.score.toFixed(0)}</span>
                )}
                {c.childBodyPctOfParentBody !== null && (
                  <span>child/parent body {c.childBodyPctOfParentBody.toFixed(0)}%</span>
                )}
                {c.parentBodyPct !== null && (
                  <span>parent body {c.parentBodyPct.toFixed(0)}%</span>
                )}
                {c.hodLodPenalty !== null && c.hodLodPenalty > 0 && (
                  <span className="crp-hodlod">
                    -{c.hodLodPenalty.toFixed(0)} near extreme
                  </span>
                )}
                {c.atrRatio !== null && <span>{c.atrRatio.toFixed(2)}&times;ATR</span>}
                {c.parentRange !== null && <span>rng {c.parentRange.toFixed(1)}</span>}
              </div>

              {c.rejection && (
                <div className="crp-fails">
                  rule rejected it: {c.rejection.detail}
                </div>
              )}

              <div className="crp-actions" onClick={(e) => e.stopPropagation()}>
                {GRADES.map((g) => (
                  <button
                    key={g}
                    className={`crp-btn ${g} ${l?.verdict === g ? 'on' : ''}`}
                    onClick={() => vote(c.id, g, reasonDraft[c.id] ?? '')}
                    title={`Mark this a ${g} setup`}
                  >
                    {g === 'super-strong' ? 'super' : g}
                  </button>
                ))}
                <button
                  className={`crp-btn no ${l?.verdict === 'not-ideal' ? 'on' : ''}`}
                  onClick={() => vote(c.id, 'not-ideal', reasonDraft[c.id] ?? '')}
                  title="Not a real setup — pick or type a reason first"
                >
                  <Ban size={11} /> no
                </button>
                {/* Only offered on a bar the ENGINE rejected. `matchesRule`
                    used to answer this and was the local scorer's verdict; the
                    engine's own outcome is what decides it now, and a bar it
                    accepted cannot have been "missed". */}
                {c.rejection && (
                  <button
                    className={`crp-btn missed ${l?.verdict === 'missed' ? 'on' : ''}`}
                    onClick={() => vote(c.id, 'missed', reasonDraft[c.id] ?? '')}
                    title="The rule should have fired here"
                  >
                    <Plus size={11} /> missed
                  </button>
                )}
              </div>

              {l?.reason && <div className="crp-reason-saved">“{l.reason}”</div>}

              {!l && autoTags[c.id] && (
                <div className="crp-auto" onClick={(e) => e.stopPropagation()}>
                  {autoTags[c.id].reason && (
                    <span className="crp-auto-reason">“{autoTags[c.id].reason}”</span>
                  )}
                  <button
                    className="crp-btn accept"
                    onClick={() =>
                      acceptAutoTag(c.id, autoTags[c.id].verdict, autoTags[c.id].reason)
                    }
                    title="Confirm this suggestion as a real label"
                  >
                    accept
                  </button>
                </div>
              )}

              {needsReason[c.id] && (
                <div className="crp-needs-reason">
                  why not? pick one below or type it — that's what fixes the rule
                </div>
              )}

              <div className="crp-reasons" onClick={(e) => e.stopPropagation()}>
                {presets.length === 0 && (
                  <span className="crp-preset-hint">
                    type a reason below — it becomes a preset
                  </span>
                )}
                {presets.map((p) => (
                  <button
                    key={p}
                    className={`crp-preset ${reasonDraft[c.id] === p ? 'on' : ''}`}
                    onClick={() => setReasonDraft((d) => ({ ...d, [c.id]: p }))}
                    title="Use this reason, then pick a verdict"
                  >
                    {p}
                  </button>
                ))}
                <input
                  className="crp-reason-input"
                  placeholder="type a reason, then pick a verdict"
                  value={reasonDraft[c.id] ?? ''}
                  onChange={(e) => setReasonDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CandidateReviewPanel;
